import { z } from 'zod';
import { SourceRef } from './source-ref.js';
import {
  AttachmentReadInput,
  AttachmentText,
  NormalizedPage,
  PageAttachmentsInput,
  PageAttachmentsOutput,
  PageChangesInput,
  PageChangesOutput,
  PageReadInput,
  PageSearchInput,
  PageSearchOutput,
} from './knowledge.js';

/**
 * Capability contracts (ADR-010). Workflows request `connection + capability`; the binding to
 * an implementation (native adapter or outbound MCP tool) is configuration. Contracts are
 * versioned and vendor-neutral: they describe what the platform needs, not any tracker's API.
 * Vendor-specific fields are carried verbatim in `rawFields` and mapped by configuration.
 */
export const CapabilityClass = z.enum(['READ', 'WRITE', 'ADMIN']);
export type CapabilityClass = z.infer<typeof CapabilityClass>;

export const IssueReadInput = z.object({
  key: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]*$/, 'issue keys contain letters, digits, "_", "." or "-"')
    .describe('Issue identifier, e.g. "PROJ-123".'),
});
export type IssueReadInput = z.infer<typeof IssueReadInput>;

export const IssueLink = z.object({
  relation: z.string(),
  direction: z.enum(['inward', 'outward']),
  key: z.string(),
  title: z.string().nullable(),
});

export const NormalizedIssue = z.object({
  key: z.string(),
  title: z.string(),
  /** Plain text (markup stripped by the adapter). */
  description: z.string().nullable(),
  status: z.string().nullable(),
  type: z.string().nullable(),
  priority: z.string().nullable(),
  labels: z.array(z.string()),
  components: z.array(z.string()),
  parentKey: z.string().nullable(),
  links: z.array(IssueLink),
  createdAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime().nullable(),
  /** Vendor field map, passed through untouched (mapping is configuration, never assumed). */
  rawFields: z.record(z.string(), z.unknown()),
  /** True when the data comes from SYNTHETIC fixtures, never from a real system. */
  synthetic: z.boolean(),
  source: SourceRef,
});
export type NormalizedIssue = z.infer<typeof NormalizedIssue>;

export interface CapabilityContract<
  I extends z.ZodType = z.ZodType,
  O extends z.ZodType = z.ZodType,
> {
  key: string;
  version: number;
  class: CapabilityClass;
  description: string;
  input: I;
  output: O;
  /** Input fields that must never be persisted in tool-call records. */
  redactArgs?: readonly string[];
}

export const CAPABILITIES = {
  'issue.read': {
    key: 'issue.read',
    version: 1,
    class: 'READ',
    description: 'Read one issue from an issue tracker and return it normalized.',
    input: IssueReadInput,
    output: NormalizedIssue,
  },
  'page.changes': {
    key: 'page.changes',
    version: 1,
    class: 'READ',
    description: 'List pages changed since a cursor under the given roots (incremental sync).',
    input: PageChangesInput,
    output: PageChangesOutput,
  },
  'page.read': {
    key: 'page.read',
    version: 1,
    class: 'READ',
    description: 'Read one page (optionally a specific version) as normalized text.',
    input: PageReadInput,
    output: NormalizedPage,
  },
  'page.attachments': {
    key: 'page.attachments',
    version: 1,
    class: 'READ',
    description: 'List the attachments of a page (name, media type, size, version).',
    input: PageAttachmentsInput,
    output: PageAttachmentsOutput,
  },
  'attachment.read': {
    key: 'attachment.read',
    version: 1,
    class: 'READ',
    description:
      'Read the text of one page attachment (PDF, DOCX, text formats), bounded; read-only.',
    input: AttachmentReadInput,
    output: AttachmentText,
  },
  'page.search': {
    key: 'page.search',
    version: 1,
    class: 'READ',
    description: 'Search pages (targeted research for knowledge gaps).',
    input: PageSearchInput,
    output: PageSearchOutput,
  },
} as const satisfies Record<string, CapabilityContract>;

export type CapabilityKey = keyof typeof CAPABILITIES;
export const CAPABILITY_KEYS = Object.keys(CAPABILITIES) as CapabilityKey[];

export function isCapabilityKey(v: string): v is CapabilityKey {
  return Object.hasOwn(CAPABILITIES, v);
}

/** `native:<adapter>` or `mcp:<server>/<tool>`. */
export const ImplementationRef = z
  .string()
  .regex(/^(native:[a-z0-9][a-z0-9-]*|mcp:[a-z0-9][a-z0-9._-]*\/[A-Za-z0-9_.-]+)$/);

// ── Admin DTOs ──────────────────────────────────────────────────────────────
export const ConnectionView = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  bindings: z.record(z.string(), z.string()),
  config: z.record(z.string(), z.unknown()),
  credentialId: z.string().uuid().nullable(),
  credentialName: z.string().nullable(),
  classification: z.enum(['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED']),
  allowedHosts: z.array(z.string()),
  status: z.enum(['active', 'disabled']),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ConnectionView = z.infer<typeof ConnectionView>;

export const ConnectionUpsert = z.object({
  name: z.string().regex(/^[a-z0-9][a-z0-9._-]{1,62}$/),
  description: z.string().max(500).default(''),
  bindings: z.record(z.string(), ImplementationRef),
  config: z.record(z.string(), z.unknown()).default({}),
  credentialId: z.string().uuid().nullable().default(null),
  classification: z.enum(['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED']).default('INTERNAL'),
  allowedHosts: z.array(z.string().min(1).max(253)).max(20).default([]),
  status: z.enum(['active', 'disabled']).default('active'),
});
export type ConnectionUpsert = z.infer<typeof ConnectionUpsert>;

export const ToolCallView = z.object({
  id: z.string().uuid(),
  stepKey: z.string().nullable(),
  connectionName: z.string(),
  capability: z.string(),
  implementation: z.string(),
  status: z.enum(['succeeded', 'failed', 'denied']),
  args: z.record(z.string(), z.unknown()),
  resultHash: z.string().nullable(),
  resultClassification: z.string().nullable(),
  durationMs: z.number().int().nullable(),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  startedAt: z.iso.datetime(),
});
export type ToolCallView = z.infer<typeof ToolCallView>;

export const AdapterView = z.object({
  id: z.string(),
  description: z.string(),
  capabilities: z.array(z.string()),
  requiresCredential: z.boolean(),
  synthetic: z.boolean(),
});
export type AdapterView = z.infer<typeof AdapterView>;
